import { useDispatch, useSelector } from 'react-redux';

import { useGroupConfigEnterprise } from './groupConfigEnterpriseController';
import { setCompaniesWithParkings, setResaClasses } from '../application/subscriptionSlice';

import type { RootState } from '#root//rootState';
import { setApiResponseMeta } from '@/common/application/apiResponseMetaSlice';
import { setApiResponseStatus } from '@/common/application/apiResponseStatusSlice';
import { setCompanies, setParkings, setParkingCtx } from '@/common/application/contextSlice';
import { handleHttpError } from '@/common/application/handleHttpError';
import { setLoader, toggleToast, unsetLoader } from '@/common/application/toastSlice';
import type { ApiResaClassStats, ResaClassStats } from '@/common/domain/stats';
import { RequestTypeEnum } from '@/common/domain/toast';
import { logException, logTrace, SeverityLevel } from '@/common/utils/logger';
import { setCompanyAdminGroup, clearPendingGroupConfigEnterprise, clearPendingResaClass, setPendingGroupConfigEnterprise, setPendingResaClass, updatePendingGroupConfigEnterpriseProps, updatePendingResaClassProps } from '@/group/application/companySlice';
import { useGroup } from '@/group/application/groupController';
import type { pGroup } from '@/group/application/groupSlice';
import useGroupApi from '@/group/data/groupApi';
import { deleteGroup, useGroupStore } from '@/group/data/groupStore';
import { EnumRelation, type Group } from '@/group/domain/group';
import type { GroupConfig, GroupConfigEnterprise } from '@/group/domain/groupConfig';
import { setParkingAdminGroup, setParking } from '@/parking/application/parkingSlice';
import useParkingApi from '@/parking/data/parkingApi';
import { useParkingStore } from '@/parking/data/parkingStore';
import type { ParkCompPair, Parking } from '@/parking/domain/parking';
import useSubscriptionApi from '@/subscription/data/subscriptionApi';
import { useSubscriptionStore } from '@/subscription/data/subscriptionStore';
import type { ReservationClass, ReservationClassProps } from '@/subscription/domain/resaClass';

export const useCompany = () => {

    const dispatch = useDispatch();
    const { apiCreateGroup, apiUpdateGroup, apiUpdateGroupConfig, apiGetGroupAdminsRT, apiRemoveMemberFromGroup, apiDeleteGroup, apiGrantAccessToGroupConfig, apiCreateGroupConfig, apiGrantAccessToGroup, apiGrantReadToGroup } = useGroupApi();
    const { apiDeleteResaClass, apiCreateResaClass, apiUpdateResaClass, apiGrantAccessToResaClass, apiGrantReadAccessToResaClass, apiGetResaClassStats } = useSubscriptionApi();
    const { apiGrantReadAccessToParking } = useParkingApi();
    const { findAdminGroupFromStore, getSubGroupsByInheritance, deleteUserGrp } = useGroup();
    const { deleteGroupConfigEnterprise } = useGroupConfigEnterprise();

    const { enterpriseGroups, getAdminById, getById } = useGroupStore();
    const { getParkings: getPkgs, } = useParkingStore();
    const { getReservationClasses, getReservationClassByClient, getReservationClassById, deleteReservationClass: deleteReservationClassInStore } = useSubscriptionStore();
    

    const currentUser = useSelector((state: RootState) => state.auth.user);

    // TODO change with new parkingStore
    const getParkings = async () => {
        try {
            dispatch(setLoader());
            const response = await getPkgs();
            dispatch(setParkings(response ?? []));
            dispatch(unsetLoader());
            return response;
        } catch (error: any) {
            dispatch(unsetLoader());
            dispatch(toggleToast({ code: error.response?.status, requestType: RequestTypeEnum.getParkings, text: error.response?.data?.error?.message }));
            logException(error);
        }
    };
    const getCompanies = async () => {
        dispatch(setCompanies([]));
        try {
            const comps = await enterpriseGroups();
            dispatch(setCompanies(comps));
            return comps;
        } catch (error: any) {
            dispatch(toggleToast({
                code: error.response?.status, requestType: RequestTypeEnum.getCompanies, text: error.response?.data?.error?.message,
                isLoadingCounter: 0
            }));
            logException(error);
        }
    };

    const getResaClassStats = async(resaClass: ReservationClass, date: string, withElectric: boolean, packetId?: number | string) : Promise<Stats | null> => {
        try {
            let response;
            if (resaClass.documentId == null || typeof resaClass.documentId == 'undefined') { 
                throw Error('no resa class documentId ');
            }
            if (packetId) {
                response = await apiGetResaClassStats(resaClass.documentId, date, withElectric, packetId);
            } else {
                response = await apiGetResaClassStats(resaClass.documentId, date, withElectric);
            }
            const apiStats: ApiResaClassStats = response.data;

            const formattedData: ResaClassStats = {
                allocatedSlotsForResa: resaClass.slotNumber,
                chargerInUse: apiStats.chargingVehicle,
                freeSlotsLeft: resaClass.slotNumber - apiStats.occupiedSlots,
                occupiedSlots: apiStats.occupiedSlots
            };
            handleHttpError(response.status);
            dispatch(setApiResponseStatus(response.status));
            return formattedData;
        } catch (error: any) {
            if (error?.status) {
                //      dispatch(toggleToast({ code: error.response?.status, requestType: RequestTypeEnum.getResaClassStats, text: error.response?.data?.error?.message }));
                logException(error, { resaClassId: resaClass.documentId });
            }
            return null;
        }
    };

    // TODO handle error with toast
    const deleteCompany = async (company: pGroup) => {
        try {

            // Retreive admin group id, either from group.admingroup or using API call
            let adminGrpIdToDelete = company?.admingroup?.documentId ?? null;

            // Check if admingroup is provided in group, if not retreive it from API
            if ((company.admingroup == null || typeof company.admingroup == 'undefined') && company.id !== null && typeof company.id !== 'undefined') {
                const adminGrpResp = await findAdminGroupFromStore(company.documentId.toString());
                const adminGrp = adminGrpResp;
                if (adminGrpResp && adminGrp && adminGrp.id !== null && adminGrp.id !== -1 && typeof adminGrp.id !== 'undefined') {
                    adminGrpIdToDelete = adminGrp.documentId;
                }
            }

            // Delete company and its admin group
            if (company.id !== null && typeof company.id !== 'undefined' && typeof adminGrpIdToDelete !== 'undefined' && adminGrpIdToDelete !== null) {
                const response = await apiDeleteGroup(company.documentId);
                await apiDeleteGroup(adminGrpIdToDelete);

                // Delete resaClass
                const resaClass = await getReservationClassByClient(company.documentId ?? company.id as number);
                if (resaClass?.documentId) await apiDeleteResaClass((resaClass.documentId));
                if (resaClass?.documentId) await deleteReservationClassInStore((resaClass.documentId));
                dispatch(deleteGroup(company.documentId));
                dispatch(deleteGroup(adminGrpIdToDelete));

                // Delete group configs if they exist
                if (Array.isArray(company?.groupConfigs) && company?.groupConfigs?.length > 0) {
                    for (const grpConfigApi of company.groupConfigs) {
                        await deleteGroupConfigEnterprise((grpConfigApi.documentId as string));
                    }
                }

                // Delete inherited groups if they exist
                const subgroups: Group[] | null = await getSubGroupsByInheritance(company.documentId.toString());
                if (subgroups?.length && subgroups.length > 0) {
                    let cpt: number = 0;
                    for (const group of subgroups) {
                        if (group.id !== null && typeof group.id !== 'undefined') {
                            await deleteUserGrp(group, group.packet?.documentId);
                            dispatch(deleteGroup(group.documentId));
                            if (response.status.code !== 200) {
                                cpt += 1;
                                logTrace('Problem deleting subgroup', SeverityLevel.Warning, { groupId: group.id });
                            }
                        }
                    }
                    if (cpt > 0) {
                        logTrace('Some subgroups were not correctly deleted for super group', SeverityLevel.Warning, { companyId: company.id, failedCount: cpt });
                    }
                }

                dispatch(setApiResponseMeta(response.meta as any));
                dispatch(toggleToast({
                    ...response.status, requestType: RequestTypeEnum.deleteCompany,
                    isLoadingCounter: 0
                }));

                return response.data;
            }
        } catch (error: any) {
            logException(error, { companyId: company.documentId ?? company.id });
            dispatch(toggleToast({ code: error.response?.status, requestType: RequestTypeEnum.deleteCompany, text: error.response?.data?.error?.message }));

            throw error;
        }
    };


    const getCompanyAdmins = async (companyId: string): Promise<Group | null> => {
        try {
            /*
             * const companyAdminGroupId = (await apiGetGroupAdminsRT(companyId));
             */
            const companyAdminGroup = await getAdminById(companyId);

            /*
             * const response = await apiGetGroup(companyAdminGroupId);
             */

            if (companyAdminGroup) dispatch(setCompanyAdminGroup(companyAdminGroup));/*
            dispatch(setApiResponseStatus(response.status));
            dispatch(setApiResponseMeta(response.meta as any));*/
            return companyAdminGroup ?? null;
        } catch (error: any) {
            dispatch(toggleToast({ code: error.response?.status, requestType: RequestTypeEnum.getCompanyAdmins, text: error.response?.data?.error?.message }));
            logException(error, { companyId });
            return null;
        }
    };
    const updateGroupConfigEnterprise = async (groupConfig: GroupConfig) => {
        
        try {
            const response = await apiUpdateGroupConfig(groupConfig);
            handleHttpError(response.status);
            dispatch(toggleToast({...response.status, requestType: RequestTypeEnum.editCompany}));
            dispatch(setApiResponseStatus(response.status));

            return response.data;
        } catch (error: any) {
            if (error?.status) {
                dispatch(toggleToast({ code: error.response?.status, requestType: RequestTypeEnum.editCompany, text: error.response?.data?.error?.message }));
            }
            logException(error, { groupConfigId: groupConfig.documentId });
        }
    };
    const getResaClasses = async () => {
        try {
            dispatch(setLoader());
            const response = await getReservationClasses();
            dispatch(unsetLoader());
            if (response) dispatch(setResaClasses(response));
            return response;
        } catch (error: any) {
            dispatch(unsetLoader());
            dispatch(toggleToast({
                code: error.response?.status, requestType: RequestTypeEnum.unknown, text: error.response?.data?.error?.message,
                isLoadingCounter: 0
            }));
            logException(error);
        }
    };

    // Widened to ReservationClassProps to match the slice's PayloadAction: callers such as
    // PricingTable's onSwitch hand back a compiled (partial) resaClass, not a complete one.
    const setPendingResaClassCont = (pendingResaClass: ReservationClassProps) => {
        dispatch(setPendingResaClass(pendingResaClass));
    };
    const updatePendingResaClassCont = (field: string, value: any) => {
        dispatch(updatePendingResaClassProps({ field, value }));
    };
    const clearPendingResaClassCont = () => {
        dispatch(clearPendingResaClass());
    };


    const fillAdminGrpForComp = async (company: Group) => {
        if (company.id) {
            const adminGrp = await getCompanyAdmins(company.id?.toString());
            return {...company, adminGroup: adminGrp};
        }
    };


    function createPairs(parkings: Parking[], companies: Group[], reservationClasses: ReservationClass[]): { parking: Parking, company: Group, resaClass: ReservationClass }[] {

        const pairs: { parking: Parking, company: Group, resaClass: ReservationClass }[] = [];

        // Create maps keyed by both numeric id and documentId to handle Strapi v5 UUID fields
        const parkingsById = new Map<number, Parking>();
        const parkingsByDocId = new Map<string, Parking>();
        const companiesById = new Map<number, Group>();
        const companiesByDocId = new Map<string, Group>();

        for (const p of parkings) {
            if (p.id) parkingsById.set(Number(p.id), p);
            if (p.documentId) parkingsByDocId.set(p.documentId, p);
        }
        for (const e of companies) {
            if (e.id) companiesById.set(Number(e.id), e);
            if (e.documentId) companiesByDocId.set(e.documentId, e);
        }

        // Iterate through ResaClass list and create pairs
        for (const r of reservationClasses) {
            const numParkingId = Number(r.parkingId);
            const p = (Number.isNaN(numParkingId) ? undefined : parkingsById.get(numParkingId))
                ?? (r.parkingId ? parkingsByDocId.get(r.parkingId) : undefined);

            const numClientId = Number(r.clientId);
            const e = (Number.isNaN(numClientId) ? undefined : companiesById.get(numClientId))
                ?? (r.clientId ? companiesByDocId.get(r.clientId) : undefined);

            if (p && e) {
                pairs.push({ company: e, parking: p, resaClass: r });
            } else {
                logTrace('createPairs: no match for resaClass', SeverityLevel.Warning, { clientIdFound: !!e, parkingIdFound: !!p, resaClassId: r.id });
            }
        }
        return pairs;
    }

    // get customers for one selected parking as FM or LA
    const getCompaniesForParking = async (parking: Parking) => {
        dispatch(setLoader());
        const resaClasses = await getResaClasses();
        const companies = await getCompanies();
        dispatch(unsetLoader());
        if (resaClasses) {
            return createPairs([parking], companies, resaClasses);
        }
    };

    const getCompaniesWithParkings = async (withAdmins?: boolean) => {

        dispatch(setLoader());
        const parkings = await getParkings();
        const resaClasses = await getResaClasses();
        const companies = await getCompanies();

        if (!(parkings && companies && resaClasses)) {

            dispatch(unsetLoader());
            return null;
        }
        const pairs = createPairs(parkings, companies, resaClasses);
        if (!pairs || pairs.length === 0 || !pairs[0]) {
            logTrace('getCompaniesWithParkings: createPairs returned no pairs', SeverityLevel.Warning);
            dispatch(setCompaniesWithParkings([]));
            dispatch(unsetLoader());
            return [];
        }

        // fill admins for companies
        if (withAdmins) {
            const newPairs: ParkCompPair[] = [];
            for (const pair of pairs) {
                const compWithAdmin = await fillAdminGrpForComp(pair.company);
                if (!compWithAdmin) continue;
                newPairs.push({company: compWithAdmin, parking: pair.parking, resaClass: pair.resaClass });
            }
            dispatch(setCompaniesWithParkings(newPairs));
        } else {
            dispatch(setCompaniesWithParkings(pairs));
        }
        dispatch(unsetLoader());
        return pairs;
    };


    const clearPendingGroupConfigEnterpriseCont = async () => {
        dispatch(clearPendingGroupConfigEnterprise());
    };


    const setPendingGroupConfigEnterpriseCont = (pendingGroupConfigEnterprise: GroupConfigEnterprise) => {
        dispatch(setPendingGroupConfigEnterprise(pendingGroupConfigEnterprise));
    };


    const updatePendingGroupConfigEnterprisePropsCont = (field: string, value: any) => {
        dispatch(updatePendingGroupConfigEnterpriseProps({ field, value }));
    };

    const setSelectedParking = (parking: Parking) => {
        dispatch(setParkingCtx(parking));
        dispatch(setParking(parking));
    };

    const getClientResaClass = async (id: string | number) => {
        const response = await getReservationClassByClient(id);
        return response;
    };


    const updateCompany = async (pendingGroupConfigEnterprise: GroupConfigEnterprise, superGroup: Group, pendingResaClass: ReservationClass, parkingId: number, groupConfigId: string): Promise<GroupConfig | undefined> => {
        try {
            const mutableGroup = { ...pendingGroupConfigEnterprise };
            if (mutableGroup.name === undefined || mutableGroup.name === null) {
                throw Error;
            }

            const updateGroupResp = await apiUpdateGroup({
                cover: superGroup.cover,
                documentId: superGroup.documentId,
                id: superGroup.id,
                name: mutableGroup.name,
            });
            if (superGroup.id) {
                const adm = await findAdminGroupFromStore(superGroup.documentId?.toString());
                if (adm?.id) {
                    await apiUpdateGroup({
                        id: adm?.id,
                        documentId: adm?.documentId,
                        name: mutableGroup.name + ' Admin',
                    });
                    await getById(adm?.documentId, true);
                }
            }
           

            if ((!updateGroupResp.data.id || updateGroupResp.data.id < 0) && !updateGroupResp.data.documentId) {
                throw Error;
            }

            const updatedGrpConfig = await apiUpdateGroupConfig({groupConfig: {
                // TODO remove these fields
                color: 'red', memberSyncSource: 'csv', ...pendingGroupConfigEnterprise,
            }, documentId: groupConfigId
            });
            if (updatedGrpConfig.status.code == 200) {
                dispatch(toggleToast({code: 200, requestType: RequestTypeEnum.updateCompany, text: '' }));
            } else {
                dispatch(toggleToast({code: updatedGrpConfig.status.code, requestType: RequestTypeEnum.updateCompany, text: updatedGrpConfig.status.text }));
            }

            if (superGroup.documentId ?? superGroup.id?.toString()) {
                await apiUpdateResaClass({ ...pendingResaClass, clientId: superGroup.documentId ?? superGroup.id?.toString(), parkingId: parkingId.toString() });
                await getReservationClassById(pendingResaClass.documentId ?? pendingResaClass.documentId?.toString(), true);
                dispatch(toggleToast({code: 200, requestType: RequestTypeEnum.updateCompany, text: ''}));
                
            }
            logTrace('Company successfully updated', SeverityLevel.Information, { companyId: updateGroupResp?.data?.id });
            if (updateGroupResp.data.documentId) await getById(updateGroupResp.data.documentId, true);

            return;

        } catch (error: any) {
            logException(error, { companyId: superGroup.documentId ?? superGroup.id });

            if (error.response.data.error.message.includes('Not enough slots')) {
                dispatch(toggleToast({ code: error.response?.status ?? -1, text: 'error.notEnoughPlacesInParking'}));
                throw new Error('notEnoughSlotsInParking');
            }
            dispatch(toggleToast({ code: error.response?.status, requestType: RequestTypeEnum.updateCompany, text: error.response?.data?.error?.message }));
            throw error;
        }
    };


    const getParkingAdmins = async (parkingId: string): Promise<Group | null> => {
        try {
            const parkingAdminGroup = (await apiGetGroupAdminsRT(parkingId));

            const response = parkingAdminGroup?.documentId ? await getById(parkingAdminGroup.documentId) : null;
            if (response) dispatch(setParkingAdminGroup(response));
            return response ?? null;
        } catch (error: any) {
            dispatch(toggleToast({ code: error.response?.status, requestType: RequestTypeEnum.getParkingAdmins, text: error.response?.data?.error?.message }));
            logException(error, { parkingId });
            return null;
        }
    };

    const createCompany = async (pendingGroupConfigEnterprise: GroupConfigEnterprise, superGroup: Group, pendingResaClass: ReservationClass, parkingId: number): Promise<Group | null> => {
        try {

            // 1) create a group with company name
            const mutableGroup = { ...pendingGroupConfigEnterprise };
            if (mutableGroup.name === undefined || mutableGroup.name === null) {
                throw Error;
            }
            const createGroupResp = await apiCreateGroup({
                cover: superGroup.cover,
                defaultDuration: 0,
                name: mutableGroup.name,
                prefix: '_subgroup',
                relatesTo: [
                    {
                        relation: EnumRelation.MEMBERSHIP,
                        targetId: String(parkingId),
                        targetModel: 'parking'
                    }
                ],
                adminGroup: undefined
            });
            if ((!createGroupResp.data?.documentId || createGroupResp.data.documentId) && !createGroupResp.data?.documentId) {
                throw Error;
            }
            logTrace('Company successfully created', SeverityLevel.Information, { companyId: createGroupResp?.data?.id });

            /*
             * Create groupAdmin
             * Create the admin group for the subgroup
             */
            const createGroupAdminResp = await apiCreateGroup({
                defaultDuration: 0,
                name: mutableGroup.name + ' Admin',
                prefix: '_subgroupAdmin',
                relatesTo: [{
                    relation: EnumRelation.OWNERSHIP,
                    targetId: (createGroupResp?.data?.documentId ?? createGroupResp?.data?.id)?.toString() ?? '',
                    targetModel: 'userGroup',
                }],
                adminGroup: undefined
            });
            logTrace('Admin group created', SeverityLevel.Information, { adminGroupId: createGroupAdminResp.data?.id });

            // 3) Create resaClass with the created group id
            const createdResaClass = await apiCreateResaClass({ ...pendingResaClass, clientId: (createGroupResp.data.documentId ?? createGroupResp.data.id)?.toString() ?? '', parkingId: parkingId.toString() });
            logTrace('Reservation class successfully created', SeverityLevel.Information, { resaClassId: createdResaClass.id });
            await getReservationClassById(createdResaClass.documentId, true);

            // Create the groupConfigEnterprise 
            const createGrpConfigResp2 = await apiCreateGroupConfig(

                // TODO remove color and memberSyncSource fields (unecessary but currently required by API)
                {
                    groupConfig: { color: 'red', memberSyncSource: 'csv', superAdminsGroup: createGroupAdminResp.data, ...pendingGroupConfigEnterprise, }
                },
                createGroupResp.data.documentId,
            );
            const createdGrpConfig2 = createGrpConfigResp2.data;
            if (createGrpConfigResp2.status.code >= 200 && createGrpConfigResp2.status.code <= 399) {
                logTrace('Group config created', SeverityLevel.Information, { groupConfigId: createdGrpConfig2?.documentId });
            } else {
                throw createGrpConfigResp2;
            }

            /*
             * Grant accesses
             * created group enterprise should access groupAdmin
             * created admin group should access enterprise
             * created group enterprise should access Admin with only reading rights
             * group Enterprise should read resaclass
             */

            const parkingAdminGroup = await getParkingAdmins(createdResaClass?.parkingId);

            // admin to comp
            if (createGroupAdminResp.data?.documentId && createGroupResp.data.documentId) await apiGrantAccessToGroup(createGroupResp.data.documentId, createGroupAdminResp.data.documentId);

            // comp readto admin
            if (createGroupAdminResp.data?.documentId && createGroupResp.data.documentId) await apiGrantReadToGroup(createGroupAdminResp.data.documentId, createGroupResp.data.documentId);
            
            // admin to admin
            if (createGroupAdminResp.data?.documentId) await apiGrantAccessToGroup(createGroupAdminResp.data.documentId, createGroupAdminResp.data.documentId);

            // comp readto parking
            if (createdResaClass?.parkingId && createGroupResp.data.documentId) await apiGrantReadAccessToParking((createdResaClass?.parkingId), createGroupResp.data.documentId);

            // comp readto resa
            if (createdResaClass?.documentId && createGroupResp.data.groupRef) await apiGrantReadAccessToResaClass((createdResaClass?.documentId), createGroupResp.data.groupRef);

            // admin readto resa
            if (createdResaClass?.documentId && createGroupAdminResp.data?.groupRef) await apiGrantReadAccessToResaClass((createdResaClass?.documentId), createGroupAdminResp.data.groupRef);

            // admin readto parking
            if (createdResaClass?.parkingId && createGroupAdminResp.data?.documentId) await apiGrantReadAccessToParking((createdResaClass?.parkingId), createGroupAdminResp.data.documentId);

            // parkingAdm to resa
            if (createdResaClass?.documentId && parkingAdminGroup?.groupRef) await apiGrantAccessToResaClass((createdResaClass?.documentId), parkingAdminGroup.groupRef);
            
            // parkingAdmin to groupConfig
            if (createdGrpConfig2?.documentId && parkingAdminGroup?.documentId) await apiGrantAccessToGroupConfig((createdGrpConfig2?.documentId), parkingAdminGroup?.documentId);
            
            // parkingAdmin to admin
            if (createGroupAdminResp.data?.documentId && parkingAdminGroup?.documentId) await apiGrantAccessToGroup(createGroupAdminResp.data.documentId, parkingAdminGroup.documentId);
            
            // group readto parkingAdmin
            if (parkingAdminGroup?.documentId && createGroupResp.data.documentId) await apiGrantReadToGroup(parkingAdminGroup?.documentId, createGroupResp.data.documentId);
            
            // admin readto parkingAdmin
            if (parkingAdminGroup?.documentId && createGroupAdminResp.data?.documentId) await apiGrantReadToGroup(parkingAdminGroup?.documentId, createGroupAdminResp.data.documentId);
            
            // parkingAdmin to group
            if (parkingAdminGroup?.documentId && createGroupResp.data.documentId) await apiGrantAccessToGroup(createGroupResp.data.documentId, parkingAdminGroup?.documentId);
            
            if (currentUser?.ref) await apiRemoveMemberFromGroup(createGroupResp.data.documentId ?? -1, currentUser?.ref);
            
            if (currentUser?.ref && createGroupAdminResp.data?.documentId) await apiRemoveMemberFromGroup(createGroupAdminResp.data.documentId ?? -1, currentUser?.ref);

            if (createGroupResp.data.documentId) await getById(createGroupResp.data.documentId, true);
            if (createGroupAdminResp.data?.documentId) await getById(createGroupAdminResp.data.documentId, true);

            dispatch(toggleToast({code: 200, requestType: RequestTypeEnum.createClient, text: ''}));

            return createGroupResp.data;
        } catch (error: any | ApiResponse<Group | null> | ApiResponse<GroupConfig>) {
            logException(error, { companyId: superGroup.documentId ?? superGroup.id });

            if (error.response.data.error.message.includes('Not enough slots')) {
                dispatch(toggleToast({ code: error.response?.status ?? -1, text: 'error.notEnoughPlacesInParking'}));
                throw new Error('notEnoughSlotsInParking');
            }
            dispatch(toggleToast({ code: error.response?.status ?? -1, requestType: RequestTypeEnum.createClient, text: error.response?.data?.error?.message ?? '' }));

            return null;
        }
    };

    return {
        clearPendingGroupConfigEnterpriseCont, clearPendingResaClassCont, createCompany, getResaClassStats,
        deleteCompany, getClientResaClass, getCompanies, getCompaniesForParking, getCompaniesWithParkings,
        getCompanyAdmins, getResaClasses, setPendingGroupConfigEnterpriseCont,
        setPendingResaClassCont, setSelectedParking, updateCompany,
        updateGroupConfigEnterprise, updatePendingGroupConfigEnterprisePropsCont, updatePendingResaClassCont
    };
};